import { makeAutoObservable, runInAction } from 'mobx';
import { ErrorStatusEnum, ImportTypeEnum } from '@app/common';
import type { RootStore } from './root-store';
import { PortfolioIntegrationMobxDto } from '../mobx/dtos/portfolio-integration/portfolio-integration-mobx-dto';
import i18n from '../../translation/i18n';
import { Trading212IntegrationDto } from '../../defs/api';

export class PortfolioIntegrationStore {
    rootStore: RootStore;

    portfolioIntegration: PortfolioIntegrationMobxDto = new PortfolioIntegrationMobxDto();

    trading212IntegrationOpened: boolean = false;

    trading212IntegrationSaved: boolean = false;

    trading212IntegrationSaving: boolean = false;

    trading212TokenInput: string = '';

    trading212SecretKeyInput: string = '';

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;

        makeAutoObservable(this, {
            rootStore: false,
        });
    }

    async fetchPortfolioIntegration() {
        try {
            const portfolioIntegration =
                await this.rootStore.apiClient.portfolioIntegrationController.portfolioIntegrationControllerGetIntegration(
                    {
                        id: this.rootStore.portfolioStore.selectedPortfolio.id,
                    },
                );

            runInAction(() => {
                this.portfolioIntegration = PortfolioIntegrationMobxDto.create(portfolioIntegration);
                this.trading212IntegrationOpened =
                    this.portfolioIntegration.hasTrading212Token && this.portfolioIntegration.hasTrading212SecretKey;
                this.clearTrading212Inputs();
                this.trading212IntegrationSaved = true;
            });
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
            this.rootStore.alertStore.setErrorMessageByStatus(e.message);
        }
    }

    async toggleTrading212PortfolioIntegrationOpened() {
        this.trading212IntegrationOpened = !this.trading212IntegrationOpened;
        if (!this.trading212IntegrationOpened) {
            await this.disconnectTrading212Integration();
        }
    }

    async saveTrading212Integration() {
        try {
            this.trading212IntegrationSaving = true;
            const trading212IntegrationDto: Trading212IntegrationDto = {};
            const trading212Token = this.trading212TokenInput.trim();
            const trading212SecretKey = this.trading212SecretKeyInput.trim();

            if (trading212Token.length > 0) {
                trading212IntegrationDto.trading212Token = trading212Token;
            }

            if (trading212SecretKey.length > 0) {
                trading212IntegrationDto.trading212SecretKey = trading212SecretKey;
            }

            const portfolioIntegration =
                await this.rootStore.apiClient.portfolioIntegrationController.portfolioIntegrationControllerUpdateTrading212Integration(
                    {
                        id: this.rootStore.portfolioStore.selectedPortfolio.id,
                        trading212IntegrationDto,
                    },
                );

            runInAction(() => {
                this.portfolioIntegration = PortfolioIntegrationMobxDto.create(portfolioIntegration);
                this.trading212IntegrationOpened =
                    this.portfolioIntegration.hasTrading212Token && this.portfolioIntegration.hasTrading212SecretKey;
                this.clearTrading212Inputs();
                this.trading212IntegrationSaved = true;
            });
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
            this.rootStore.alertStore.setErrorMessageByStatus(e.message);
        } finally {
            this.trading212IntegrationSaving = false;
        }
    }

    async disconnectTrading212Integration() {
        try {
            this.trading212IntegrationSaving = true;
            const portfolioIntegration =
                await this.rootStore.apiClient.portfolioIntegrationController.portfolioIntegrationControllerUpdateTrading212Integration(
                    {
                        id: this.rootStore.portfolioStore.selectedPortfolio.id,
                        trading212IntegrationDto: {
                            trading212Token: null,
                            trading212SecretKey: null,
                        },
                    },
                );

            runInAction(() => {
                this.portfolioIntegration = PortfolioIntegrationMobxDto.create(portfolioIntegration);
                this.trading212IntegrationOpened = false;
                this.clearTrading212Inputs();
                this.trading212IntegrationSaved = true;
            });
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
            this.rootStore.alertStore.setErrorMessageByStatus(e.message);
        } finally {
            this.trading212IntegrationSaving = false;
        }
    }

    updateTrading212TokenInput(trading212Token: string) {
        this.trading212IntegrationSaved = false;
        this.trading212TokenInput = trading212Token;
    }

    updateTrading212SecretKeyInput(trading212SecretKey: string) {
        this.trading212IntegrationSaved = false;
        this.trading212SecretKeyInput = trading212SecretKey;
    }

    clearTrading212Inputs() {
        this.trading212TokenInput = '';
        this.trading212SecretKeyInput = '';
    }

    async runTrading212Integration() {
        this.rootStore.tradeStore.tradeImportDialogStore.importResultMessage = null;
        this.rootStore.tradeStore.tradeImportDialogStore.importResultWarnings = [];

        try {
            this.rootStore.tradeStore.tradeImportDialogStore.startLoading();
            const response =
                await this.rootStore.apiClient.tradeImportController.tradeImportControllerRunTrading212Integration({
                    id: this.rootStore.portfolioStore.selectedPortfolio.id,
                    processTrades: this.rootStore.tradeStore.tradeImportDialogStore.recalculateAfterImport,
                });

            if (!response) {
                throw new Error(ErrorStatusEnum.NoResponseFromTradeImport);
            }

            if (response.error) {
                throw new Error(response.error);
            }

            if (response.importedRowsCount <= 0) {
                if (response.existingTradesCount > 0) {
                    this.rootStore.tradeStore.tradeImportDialogStore.importResultWarnings.push({
                        message: i18n.t(`blocks.trades.allRowsExisted`),
                    });
                    return;
                }

                this.rootStore.tradeStore.tradeImportDialogStore.importResultWarnings.push({
                    message: i18n.t(`blocks.trades.noValidRows`),
                });
            }

            let importedResultMessage = i18n.t(`blocks.trades.imported.successfully`, {
                importedRowsCount: response.importedRowsCount,
            });
            if (response.existingTradesCount) {
                importedResultMessage += i18n.t(`blocks.trades.imported.existed`, {
                    existingTradesCount: response.existingTradesCount,
                });
            }

            if (response.notFoundCurrencies.length) {
                this.rootStore.tradeStore.tradeImportDialogStore.importResultWarnings.push({
                    message: i18n.t(`blocks.trades.notFoundCurrencies`, {
                        currencies: response.notFoundCurrencies.join(', '),
                    }),
                });
            }

            if (response.notFoundSymbols.length) {
                this.rootStore.tradeStore.tradeImportDialogStore.importResultWarnings.push({
                    message: i18n.t(`blocks.trades.notFoundSymbols`, {
                        symbols: response.notFoundSymbols.join(', '),
                    }),
                    link: '/app/faq#nerozpoznane-symboly-v-importu',
                });
            }

            if (response.invalidRowDates) {
                this.rootStore.tradeStore.tradeImportDialogStore.importResultWarnings.push({
                    message: i18n.t(`blocks.trades.invalidRowDates`, {
                        invalidRowDates: response.invalidRowDates,
                    }),
                });
            }

            if (response.hasBlacklistedTransactions) {
                this.rootStore.tradeStore.tradeImportDialogStore.importResultWarnings.push({
                    message: i18n.t(`blocks.trades.blacklistedOperations`),
                });
            }

            runInAction(() => {
                this.rootStore.tradeStore.tradeImportDialogStore.importResultMessage = importedResultMessage;
                if (this.rootStore.tradeStore.tradeImportDialogStore.recalculateAfterImport) {
                    this.rootStore.tradeStore.tradeImportDialogStore.rootStore.portfolioStore.selectedPortfolio.setIsRecalculating(
                        true,
                    );

                    if (this.rootStore.userStore.settings?.showPortfolioValueCalculationInfo) {
                        this.rootStore.portfolioValueCalculationInfoDialogStore.open();
                    }
                } else {
                    this.rootStore.portfolioStore.fetchPortfolioList();
                }
                this.rootStore.tradeStore.lastImportedBroker = ImportTypeEnum.Trading212;
                this.rootStore.tradeStore.fetchTradeList();
            });
        } catch (e) {
            this.rootStore.alertStore.setErrorMessageByStatus(e.message);
            // eslint-disable-next-line no-console
            console.error(e);
        } finally {
            this.rootStore.tradeStore.tradeImportDialogStore.stopLoading();
            this.rootStore.tradeStore.tradeImportDialogStore.closeImportDialog();
        }
    }
}
